'use client'

import { usePathname } from 'next/navigation'
import { NavigationGuardProvider as _NavigationGuardProvider } from 'next-navigation-guard'
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'

import { NavigationGuardContext } from '@/shared/model'

type Props = {
  children: ReactNode
}

export const NavigationGuardProvider = ({ children }: Props) => {
  const pathname = usePathname()
  const [guardEnabled, setGuardEnabled] = useState(true)

  const activateNavigationGuard = useCallback(() => {
    setGuardEnabled(true)
  }, [])

  const disableNavigationGuard = useCallback(
    ({ callback }: { callback?: () => void } = {}) => {
      setGuardEnabled(false)

      requestAnimationFrame(() => {
        callback?.()
      })
    },
    [],
  )

  useEffect(() => {
    activateNavigationGuard()

    return () => {
      disableNavigationGuard()
    }
  }, [pathname, activateNavigationGuard, disableNavigationGuard])

  const value = useMemo(() => {
    return {
      guardEnabled,
      activateNavigationGuard,
      disableNavigationGuard,
    }
  }, [guardEnabled, activateNavigationGuard, disableNavigationGuard])

  return (
    <_NavigationGuardProvider>
      <NavigationGuardContext.Provider value={value}>
        {children}
      </NavigationGuardContext.Provider>
    </_NavigationGuardProvider>
  )
}
